import { Box, useTheme } from '@mui/material'
import { DateTime } from 'luxon'
import { useTranslation } from 'react-i18next'
import { OptimizerSchedule } from '../../types/ports'
import { planSegments } from '../../classes/appliances/heat-pump-plan'
import colors from '../../utils/colors'

export interface HeatPumpPlanBarProps {
  schedule: OptimizerSchedule
}

/**
 * The heat pump's planned power level ahead as a bar chart: bar height is the
 * planned level (0..7 shown as 0..100 %), colour the reason as in the plan list.
 * Stretches where the heat pump is released to its own control (no plan) are
 * drawn as a dim full-height band.
 * Geometry goes through inline `style`, never `sx` (see RelayBar on why); the
 * colour comes from a small fixed set and can stay in `sx`.
 */
export function HeatPumpPlanBar(props: HeatPumpPlanBarProps): React.ReactElement {
  const { schedule } = props
  const theme = useTheme()
  const { t } = useTranslation()
  const { from, to, segments } = planSegments(schedule, Date.now())

  if (segments.length === 0) {
    return <Box sx={{ fontSize: '85%' }}>{t('No plan')}</Box>
  }

  const span = to - from
  const label = (ms: number) => DateTime.fromMillis(ms).toLocal().toFormat('HH:mm')

  return (
    <Box data-cy="box-Heat pump plan">
      <Box sx={{ position: 'relative', width: '100%', height: '2rem', borderBottom: `1px solid ${theme.palette.dimColor.main}` }}>
        {segments.map((seg, idx) =>
          <Box
            key={idx}
            title={`${label(seg.start)} ${seg.released ? t('HeatPump-Strategy-MANUAL') : `${seg.level} %`}`}
            style={{
              left: `${(seg.start - from) / span * 100}%`,
              width: `${(seg.end - seg.start) / span * 100}%`,
              height: seg.released ? '100%' : `${seg.level > 0 ? Math.max(seg.level, 8) : 0}%`,
            }}
            sx={seg.released
              ? { position: 'absolute', bottom: 0, backgroundColor: 'grey', opacity: 0.25 }
              : { position: 'absolute', bottom: 0, backgroundColor: colors.reason(seg.reason) || 'primary.main' }}
          />
        )}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', fontSize: '75%' }}>
        <span>{label(from)}</span>
        <span>{label(from + span / 2)}</span>
        <span>{label(to)}</span>
      </Box>
    </Box>
  )
}
